// The year filter is compared with loose `!=` against a string column, so a
// numeric term is a supported input and must not be coerced to a string here.
const trimIfString = term => (typeof term === 'string' ? term.trim() : term)

/**
 * Client-side filtering for the embeddable map.
 *
 * Extracted from the `mapItems` computed property of
 * `app/javascript/views/EmbedMap.vue`. The behavior is otherwise preserved
 * as-is (loose `!=` on year, exact-match on artist/commissioner vs
 * substring-match on city). Any change here changes what the shipped widget
 * renders.
 *
 * Filter terms are trimmed because they are authored input -- a widget
 * attribute or a query-string value -- where stray whitespace is an accident
 * that would otherwise silently render an empty map. The art data is NOT
 * trimmed: it comes from the database and is the same text the server-side
 * SQL filtering in `arts#index` matches against, so normalizing it here would
 * make the client and the server disagree.
 *
 * @param {Array<Object>} arts    Full map dataset (see `Art.map_data_json`).
 * @param {Object} filters        Active filters. Recognized keys: `city`,
 *                                `year`, `artist`, `commissioner`, `tag`,
 *                                `category`. Falsy values are ignored.
 * @returns {Array<Object>} The subset of `arts` matching every active filter.
 */
export function filterArts(arts, filters) {
  return arts.filter(art => {
    // City Filter (Case-insensitive partial match to mimic SQL 'LIKE')
    if (filters.city) {
      if (!art.city) return false
      const cityTerm = filters.city.trim().toLowerCase()
      const artCity = art.city.toLowerCase()
      if (!artCity.includes(cityTerm)) return false
    }

    // Year Filter
    if (filters.year && art.year != trimIfString(filters.year)) return false

    // Artist Filter (Case-insensitive check)
    if (filters.artist) {
      const artistTerm = filters.artist.trim().toLowerCase()
      const hasArtist =
        art.artists && art.artists.some(a => a.name.toLowerCase() === artistTerm)
      if (!hasArtist) return false
    }

    // Commissioner Filter (Case-insensitive check)
    if (filters.commissioner) {
      const commTerm = filters.commissioner.trim().toLowerCase()
      const hasCommissioner =
        art.commissioners &&
        art.commissioners.some(c => c.name.toLowerCase() === commTerm)
      if (!hasCommissioner) return false
    }

    // Tag Filter (Case-insensitive check)
    if (filters.tag) {
      const tagTerm = filters.tag.trim().toLowerCase()
      const hasTag = art.tags && art.tags.some(t => t.toLowerCase() === tagTerm)
      if (!hasTag) return false
    }

    // Category Filter (Case-insensitive check)
    if (filters.category) {
      const catTerm = filters.category.trim().toLowerCase()
      const hasCategory =
        art.categories &&
        art.categories.some(c => c.toLowerCase() === catTerm)
      if (!hasCategory) return false
    }

    return true
  })
}

export default filterArts
